import React from "react";
import { Building2, CheckCircle, Clock, Filter, GraduationCap, Layers, List, School, Users } from "lucide-react";

export type ExamTypeFilter = "" | "yks" | "lgs" | "publisher";
export type ExamAudienceFilter = "" | "internal" | "external";
export type ExamStatusFilter = "" | "with-results" | "without-results";

export interface ExamListFilters {
  type: ExamTypeFilter;
  audience: ExamAudienceFilter;
  gradeLevel: number | null;
  status: ExamStatusFilter;
}

export const EMPTY_EXAM_FILTERS: ExamListFilters = { type: "", audience: "", gradeLevel: null, status: "" };

export interface ExamListFacets {
  total: number;
  type: { yks: number; lgs: number; publisher: number };
  audience: { internal: number; external: number };
  grades: Array<{ grade: number; count: number }>;
}

interface ExamListFilterProps {
  filters: ExamListFilters;
  facets: ExamListFacets | null;
  onChange: (next: ExamListFilters) => void;
  /** Mobil overlay'de tam genişlik */
  fullWidth?: boolean;
  /** Mobil overlay'de seçim sonrası kapat */
  onClose?: () => void;
}

type Option<T> = { key: T; label: string; icon: React.ComponentType<{ className?: string }>; count?: number };

/**
 * "Tüm Denemeler" süzgeç paneli (Görevler sayfasındaki TaskStatusFilter kalıbı): tür, kapsam, sınıf ve sonuç durumu.
 * Sayılar seçili süzgeçten bağımsız, bütün denemeler üzerindendir.
 */
export default function ExamListFilter({ filters, facets, onChange, fullWidth = false, onClose }: ExamListFilterProps) {
  const hasExternal = Boolean(facets && facets.audience.external > 0);
  const grades = facets?.grades || [];
  const active = Boolean(filters.type || filters.audience || filters.gradeLevel || filters.status);

  const section = <T extends string | number | null>(
    title: string,
    options: Option<T>[],
    selected: T,
    apply: (value: T) => ExamListFilters
  ) => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-2">{title}</label>
      <div className="space-y-1">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = selected === option.key;
          return (
            <button
              key={String(option.key)}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(apply(option.key));
                onClose?.();
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left ${isSelected ? "bg-blue-50 text-blue-700 border border-blue-200" : "text-gray-700 hover:bg-gray-100"
                }`}>
              <Icon className={`w-4 h-4 ${isSelected ? "text-blue-600" : "text-gray-500"}`} />
              <span className="flex-1">{option.label}</span>
              {option.count !== undefined && <span className={`text-xs ${isSelected ? "text-blue-600" : "text-gray-400"}`}>{option.count}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={fullWidth ? "w-full" : "pr-2 w-64"}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-800">Filtreler</span>
        </div>
        {active && (
          <button
            type="button"
            onClick={() => { onChange(EMPTY_EXAM_FILTERS); onClose?.(); }}
            className="text-xs text-blue-600 hover:underline">
            Temizle
          </button>
        )}
      </div>

      <div className="space-y-4">
        {section<ExamTypeFilter>("Tür", [
          { key: "", label: "Tümü", icon: List, count: facets?.total },
          { key: "yks", label: "YKS", icon: GraduationCap, count: facets?.type.yks },
          { key: "lgs", label: "LGS", icon: School, count: facets?.type.lgs },
          ...(facets?.type.publisher ? [{ key: "publisher" as ExamTypeFilter, label: "Yayınevi sistemi", icon: Layers, count: facets.type.publisher }] : []),
        ], filters.type, (type) => ({ ...filters, type }))}

        {hasExternal && section<ExamAudienceFilter>("Kapsam", [
          { key: "", label: "Tümü", icon: List },
          { key: "internal", label: "Kurum içi", icon: Building2, count: facets?.audience.internal },
          { key: "external", label: "Kurum dışı", icon: Users, count: facets?.audience.external },
        ], filters.audience, (audience) => ({ ...filters, audience }))}

        {grades.length > 0 && section<number | null>("Sınıf", [
          { key: null, label: "Tümü", icon: List },
          ...grades.map((row) => ({ key: row.grade, label: `${row.grade}. sınıf`, icon: GraduationCap, count: row.count })),
        ], filters.gradeLevel, (gradeLevel) => ({ ...filters, gradeLevel }))}

        {section<ExamStatusFilter>("Sonuç", [
          { key: "", label: "Tümü", icon: List },
          { key: "with-results", label: "Sonucu yüklenmiş", icon: CheckCircle },
          { key: "without-results", label: "Sonuç bekleyen", icon: Clock },
        ], filters.status, (status) => ({ ...filters, status }))}
      </div>
    </div>
  );
}
